/**
 * The scheduling rules, shared by the API and the booking UI. Keeping them in
 * one place is what stops the calendar from offering a slot the server will
 * then refuse.
 */

/** 24h slot times, index-aligned with the `Booking.timeSlots` messages array. */
export const SLOT_TIMES = [
  "09:00",
  "10:30",
  "12:00",
  "13:30",
  "15:00",
  "16:30",
] as const;

/** Consultations are scheduled in Saudi time, wherever the visitor happens to be. */
export const BUSINESS_TIMEZONE = "Asia/Riyadh";

/** Friday. `Date.getDay()` numbering. */
const CLOSED_WEEKDAY = 5;

/** How far ahead the calendar may be browsed, in days. */
export const BOOKING_HORIZON_DAYS = 120;

export type DayKey = string; // YYYY-MM-DD

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export function isDayKey(value: string): boolean {
  if (!DATE_PATTERN.test(value)) return false;
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

export function isSlotTime(value: string): value is (typeof SLOT_TIMES)[number] {
  return (SLOT_TIMES as readonly string[]).includes(value);
}

/** The unique key a held slot is stored under. */
export function slotKeyOf(day: DayKey, time: string): string {
  return `${day}T${time}`;
}

/** A local Date → "YYYY-MM-DD", without stepping through UTC. */
export function toDayKey(date: Date): DayKey {
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

export function dayKeyToDate(day: DayKey): Date {
  const [year, month, date] = day.split("-").map(Number);
  return new Date(year, month - 1, date);
}

export function isClosedDay(day: DayKey): boolean {
  const [year, month, date] = day.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, date)).getUTCDay() === CLOSED_WEEKDAY;
}

/** "Now" as it reads on a Riyadh wall clock, so both sides agree on the past. */
export function businessNow(at: Date = new Date()): {
  day: DayKey;
  time: string;
} {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: BUSINESS_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(at);

  const get = (type: string) =>
    parts.find((part) => part.type === type)?.value ?? "00";

  return {
    day: `${get("year")}-${get("month")}-${get("day")}`,
    time: `${get("hour")}:${get("minute")}`,
  };
}

/** Slots earlier today are gone; days before today are gone entirely. */
export function isPast(
  day: DayKey,
  time: string,
  now: { day: DayKey; time: string },
): boolean {
  if (day < now.day) return true;
  if (day > now.day) return false;
  return time <= now.time;
}

export function isWithinHorizon(
  day: DayKey,
  now: { day: DayKey; time: string },
): boolean {
  const limit = dayKeyToDate(now.day);
  limit.setDate(limit.getDate() + BOOKING_HORIZON_DAYS);
  return dayKeyToDate(day).getTime() <= limit.getTime();
}

/** Whether a day can hold a booking at all, before taken slots are considered. */
export function isBookableDay(
  day: DayKey,
  now: { day: DayKey; time: string },
): boolean {
  return (
    isDayKey(day) &&
    !isClosedDay(day) &&
    day >= now.day &&
    isWithinHorizon(day, now)
  );
}
